// UPLOAD-PREVIEW-2: tile gallery before upload. Tiles is the default.
// List keeps the old row (name, size, remove). Thumbs load when a tile is in view.
import React, { useEffect, useRef, useState } from "react";
import { File, FileText, Image as ImageIcon, X } from "lucide-react";
import { getToken } from "@/lib/api";
import {
  displayName, fileType, formatBytes, makeThumbnail, makeUploadPdfThumbnail, previewKind,
} from "@/lib/filePreview";

const PAGE = 60;

function authToken() {
  return typeof getToken === "function" ? getToken() : "";
}

export function LazyThumb({ file, src, kind, bytes, className }) {
  const ref = useRef(null);
  const urlRef = useRef("");
  const [inView, setInView] = useState(false);
  const [url, setUrl] = useState("");
  const [miss, setMiss] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver !== "function") {
      setInView(true);
      return undefined;
    }
    const obs = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) setInView(true);
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!inView) return undefined;
    let dead = false;
    const forget = (next) => { if (next) URL.revokeObjectURL(next); };
    if (file && (kind === "image" || kind === "pdf")) {
      makeThumbnail(file).then((next) => {
        if (dead || !next) {
          forget(next);
          if (!dead) setMiss(true);
          return;
        }
        urlRef.current = next;
        setUrl(next);
      }).catch(() => { if (!dead) setMiss(true); });
      return () => {
        dead = true;
        if (urlRef.current) {
          URL.revokeObjectURL(urlRef.current);
          urlRef.current = "";
        }
      };
    }
    if (!file && kind === "pdf" && src) {
      makeUploadPdfThumbnail(src, authToken(), bytes, 40)
        .then((canvas) => {
          if (!dead && canvas && ref.current) ref.current.appendChild(canvas);
        })
        .catch(() => {});
      return () => { dead = true; };
    }
    return undefined;
  }, [inView, file, kind, src]);

  const showServerImage = !file && kind === "image" && inView && src && !url;
  return (
    <span ref={ref} className={`relative flex items-center justify-center overflow-hidden bg-slate-100 ${className || ""}`}>
      {url ? <img src={url} alt="" className="h-full w-full object-cover" /> : null}
      {showServerImage ? <img src={src} alt="" className="h-full w-full object-cover" /> : null}
      {miss && kind === "image" ? (
        <span className="px-1 text-center text-[10px] leading-tight text-slate-500">Preview after upload</span>
      ) : null}
      {!url && !showServerImage && !(miss && kind === "image") ? (
        kind === "image"
          ? <ImageIcon className="h-4 w-4 text-indigo-500" />
          : <FileText className="h-4 w-4 text-slate-400" />
      ) : null}
    </span>
  );
}

function RemoveButton({ name, onRemove }) {
  return (
    <button
      type="button"
      aria-label={`Remove ${name}`}
      onClick={(event) => { event.stopPropagation(); onRemove(); }}
      className="p-1 text-slate-300 hover:text-red-500"
    >
      <X className="h-3.5 w-3.5" />
    </button>
  );
}

export default function UploadGallery({ files, uploading, onRemove, onOpen }) {
  const [mode, setMode] = useState("tiles");
  const [shown, setShown] = useState(PAGE);
  if (!files || !files.length) return null;
  const visible = files.slice(0, shown);
  const count = mode === "tiles" ? Math.min(shown, files.length) : files.length;

  return (
    <div className="mt-4" data-testid="upload-gallery">
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-xs text-slate-500" data-testid="gallery-count">
          {`Showing ${count} of ${files.length}`}
        </p>
        <div className="flex overflow-hidden rounded-lg border border-slate-200">
          <button type="button" data-testid="view-list" aria-pressed={mode === "list"} onClick={() => setMode("list")} className={`px-2 py-1 text-xs ${mode === "list" ? "bg-slate-800 text-white" : "bg-white text-slate-600"}`}>List</button>
          <button type="button" data-testid="view-tiles" aria-pressed={mode === "tiles"} onClick={() => setMode("tiles")} className={`px-2 py-1 text-xs ${mode === "tiles" ? "bg-slate-800 text-white" : "bg-white text-slate-600"}`}>Tiles</button>
        </div>
      </div>
      {mode === "list" ? (
        <div className="max-h-64 space-y-2 overflow-y-auto">
          {files.map((file, index) => {
            const name = displayName(file);
            return (
              <div key={`${name}-${index}`} data-testid={`upload-list-row-${index}`} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2">
                {fileType(file).startsWith("image/") ? <ImageIcon className="h-4 w-4 shrink-0 text-indigo-500" /> : <File className="h-4 w-4 shrink-0 text-slate-400" />}
                <span className="flex-1 truncate text-sm text-slate-700">{name}</span>
                <span className="shrink-0 text-xs text-slate-400">{formatBytes(file.size)}</span>
                {uploading ? null : <RemoveButton name={name} onRemove={() => onRemove(index)} />}
              </div>
            );
          })}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2 md:grid-cols-6">
            {visible.map((file, index) => {
              const name = displayName(file);
              const kind = previewKind(file);
              return (
                <div key={`${name}-${file.size}-${index}`} className="relative rounded-xl border border-slate-200 bg-white p-1.5">
                  <button
                    type="button"
                    data-testid={`upload-tile-${index}`}
                    onClick={() => onOpen(index)}
                    onKeyDown={(event) => { if (event.key === "Enter") onOpen(index); }}
                    className="w-full text-left"
                  >
                    <LazyThumb file={file} kind={kind} className="mb-1 h-16 w-full rounded-lg" />
                    <span className="block truncate text-xs text-slate-700">{name}</span>
                    <span className="block truncate text-[10px] text-slate-400">{formatBytes(file.size)} {fileType(file) || "file"}</span>
                  </button>
                  {uploading ? null : (
                    <span className="absolute right-1 top-1">
                      <RemoveButton name={name} onRemove={() => onRemove(index)} />
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          {shown < files.length ? (
            <button type="button" data-testid="gallery-show-more" onClick={() => setShown((value) => value + PAGE)} className="mt-3 text-sm text-blue-600">
              Show more
            </button>
          ) : null}
        </>
      )}
    </div>
  );
}
